import { useState, useEffect } from 'react'

import { useLazyQuery } from '@apollo/client'
import { ActionIcon, Collapse, Loader } from '@mantine/core'
import { PDF_MIME_TYPE } from '@mantine/dropzone'
import { Check, X } from 'lucide-react'

import { useQuery } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { PDFViewer } from 'src/components/DataDisplay/PDFViewer/PDFViewer'
import DnUpload from 'src/components/Inputs/DnUpload/DnUpload'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_DOCUMENT_DOWNLOAD_URL } from 'src/graphql/documents.graphql'
import { GET_STEPS } from 'src/graphql/steps.graphql'
import { DocumentCategory } from 'src/graphql/types/documents'
import { StepType } from 'src/graphql/types/steps'
import useUploadDocument from 'src/hooks/useUploadDocument/useUploadDocument'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconEdit from 'src/icons/IconEdit'

const ResumeCard = ({ applicantId }) => {
  const [opened, setOpened] = useState(true)
  const [editing, setEditing] = useState(false)
  const [documentUrl, setDocumentUrl] = useState(null)
  const [newResumeFile, setNewResumeFile] = useState<File[]>([])

  // Only the DOT_APP step is needed here — fetching it directly keeps this
  // card off the applicant query, whose steps subtree was removed to keep it
  // lean (see GET_SIDEBAR_CHECKLIST_STEPS in steps.graphql.tsx).
  const stepsQueryOptions = {
    query: GET_STEPS,
    variables: {
      applicantId,
      filters: { OR: [{ type: StepType.DOT_APP }] },
    },
  }
  const { data, loading, refetch } = useQuery(GET_STEPS, {
    variables: stepsQueryOptions.variables,
  })

  // Find the DOT_APP step
  const dotAppStep = data?.steps?.items?.find(
    (step) => step.type === StepType.DOT_APP
  )

  // Get the latest resume document from DOT_APP step
  const resumeDocument = dotAppStep?.documents?.[0]

  const { handleCreateDocuments, isUploading } = useUploadDocument(
    [stepsQueryOptions],
    applicantId,
    dotAppStep,
    null, // voe
    DocumentCategory.DOT_APP
  )

  const [
    getDocumentUrl,
    { data: documentUrlResponse, loading: loadingDocumentUrl },
  ] = useLazyQuery(GET_DOCUMENT_DOWNLOAD_URL, {
    onCompleted: (data) => {
      if (data?.documentDownloadUrl?.url) {
        setDocumentUrl(data.documentDownloadUrl.url)
      }
    },
    onError: () => {
      toast('Unable to load resume preview', 'error')
    },
  })

  // Fetch document URL when resume is available
  useEffect(() => {
    if (resumeDocument?.documentId && applicantId && !editing) {
      getDocumentUrl({
        variables: {
          id: resumeDocument.documentId,
          applicantId: applicantId,
        },
      })
    }
  }, [resumeDocument?.documentId, applicantId, editing])

  const handleEdit = () => {
    setEditing(true)
  }

  const handleCancel = () => {
    setNewResumeFile([])
    setEditing(false)
  }

  const handleSave = async () => {
    if (newResumeFile.length === 0) {
      toast('Please select a resume file to upload', 'error')
      return
    }

    await handleCreateDocuments(newResumeFile, () => {
      toast('Resume uploaded successfully', 'success')
      setNewResumeFile([])
      setEditing(false)
      refetch()
    })
  }

  return (
    <div className="flex w-full flex-col gap-2 rounded-lg border border-doubleNickel-gray-200 p-3">
      <div
        className="text-md flex w-full cursor-pointer flex-row items-center gap-2 font-semibold text-doubleNickel-gray-900"
        onClick={() => setOpened((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpened((prev) => !prev)
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={opened}
      >
        <ActionIcon variant="subtle" className="pointer-events-none">
          <IconChevronDown
            className={`h-4 w-4 ${
              !opened && '-rotate-90'
            } fill-none stroke-doubleNickel-gray-500 transition-all`}
          />
        </ActionIcon>
        Resume
        {!editing ? (
          <DNButton
            variant="transparent"
            onClick={(e) => {
              e.stopPropagation()
              if (!opened) setOpened(true)
              handleEdit()
            }}
            className="ml-auto text-doubleNickel-gray-700"
          >
            <IconEdit className="h-3.5 w-3.5 fill-none stroke-current" />
            Edit
          </DNButton>
        ) : (
          <div className="ml-auto flex items-center gap-2">
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleSave()
              }}
              disabled={isUploading || newResumeFile.length === 0}
              className="text-doubleNickel-success-500"
            >
              <Check size={14} />
              Save
            </DNButton>
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleCancel()
              }}
              disabled={isUploading}
              className="text-doubleNickel-error-500"
            >
              <X size={14} />
              Cancel
            </DNButton>
            {isUploading && <Loader size={12} color="gray" />}
          </div>
        )}
      </div>
      <Collapse in={opened} transitionDuration={400}>
        <div className="flex flex-col gap-2">
          {editing ? (
            <div className="py-2">
              <DnUpload
                files={newResumeFile}
                onDrop={(files) => setNewResumeFile(files)}
                onRemove={() => setNewResumeFile([])}
                maxSizeMB={5}
                maxFiles={1}
                accept={PDF_MIME_TYPE}
                uploadText="Click to upload"
                fileTypesText="PDF only, 5 MB maximum"
                dragText="Drag and drop resume or browse computer"
              />
            </div>
          ) : loading || loadingDocumentUrl ? (
            <div className="flex items-center justify-center py-4">
              <Loader size="sm" />
            </div>
          ) : resumeDocument ? (
            <div className="flex flex-col gap-2">
              {documentUrl ? (
                <div className="h-[1000px] w-full overflow-hidden rounded border border-doubleNickel-gray-200">
                  <PDFViewer url={documentUrl} />
                </div>
              ) : (
                <div className="py-4 text-center text-sm text-doubleNickel-gray-500">
                  Unable to load preview
                </div>
              )}
            </div>
          ) : (
            <div className="py-4 text-center text-sm text-doubleNickel-gray-500">
              No resume uploaded
            </div>
          )}
        </div>
      </Collapse>
    </div>
  )
}

export default ResumeCard
